import {SITE, SiteId} from 'features/ui/enums';
import * as React from 'react';
import {FormattedMessage} from 'react-intl';

import {
    ConceptType,
    ObjectStatus,
    PremiseStatus,
    RentalStatus,
    StageStatus
} from 'features/search/filters/enums';
import {getCurrentSite} from 'features/services/utils/env-helper';
import {getFormattedMessage} from 'features/services/utils/language-helper';

import './style.scss';
import {CaseStatus} from 'features/errands/enums';
import { TagColor, Tags, TagDesign } from '@atoms/Tags/Tags';

interface Props {
    concept?: string;
    stageStatus?: string;
    rentalStatus?: string;
    premisesStatus?: string;
    objectStatus?: string;
    siteId?: string;
    hideType?: boolean;
    referenceProjectType?: string;
    constructionForm?: string;
    prio?: number;
    errandStatus?: CaseStatus;
}

export const ResidenceTags: React.FunctionComponent<Props> = ({
    siteId, hideType,
    concept, stageStatus, objectStatus, 
    rentalStatus, premisesStatus, referenceProjectType, constructionForm: _constructionForm,
    prio, errandStatus
}) => {
    const getConceptLabel = () => {
        switch (concept) {
            case ConceptType.PREMIUM:
                return (
                    <Tags 
                        tagDesign={TagDesign.OLD} 
                        tagColor={TagColor.PREMIUM}
                    />
                );
            case ConceptType.SMARTA_KVADRAT:
                return (
                    <Tags 
                        tagDesign={TagDesign.OLD} 
                        tagColor={TagColor.SMARTA}
                    />
                );
            default:
                return null;
        }
    };

    const getSiteLabel = () => {
        const currentSite = getCurrentSite();
        switch (siteId) {
            case SiteId.SENIORGARDEN:
                if (currentSite == SITE.SENIORGARDEN) return null;
                return (
                    <Tags 
                        tagDesign={TagDesign.OLD} 
                        tagText='Seniorgården'
                        className='tag--senior'
                    />
                );
            case SiteId.BORATT:
                if (currentSite == SITE.BORATT) return null;
                return (
                    <Tags 
                        tagDesign={TagDesign.OLD} 
                        tagText='Borätt'
                        className='tag--boratt'
                    />
                );
            default:
                return null;
        }
    };

    const renderErrandStatusTag = () => {
        switch (errandStatus) {
            case CaseStatus.REGISTERED:
            case CaseStatus.REGISTERED_FI:
            case CaseStatus.REGISTERED_NO:
                return (<Tags 
                        tagDesign={TagDesign.OLD} 
                        status={CaseStatus.REGISTERED}
                    />);
            case CaseStatus.ONGOING:
            case CaseStatus.ONGOING_NO:
            case CaseStatus.ONGOING_FI:
            case CaseStatus.NOT_RESPONDING:
            case CaseStatus.NOT_RESPONDING_FI:
            case CaseStatus.WAITING_ON_CUSTOMER:
            case CaseStatus.WAITING_ON_CUSTOMER_FI:
            case CaseStatus.WAITING_ON_DECISION:
            case CaseStatus.WAITING_ON_DECISION_FI:
            case CaseStatus.WAITING_ON_SUBCONTRACTOR:
            case CaseStatus.WAITING_ON_SUBCONTRACTOR_FI:
            case CaseStatus.WAITING_ON_SUBCONTRACTOR_NO_PORTAL:
            case CaseStatus.WAITING_ON_WEATHER:
            case CaseStatus.WAITING_ON_WEATHER_FI:
            case CaseStatus.BOOKED:
            case CaseStatus.BOOKED_FI:
            case CaseStatus.WAITING_ON_MATERIAL:
            case CaseStatus.WAITING_ON_MATERIAL_FI:
            case CaseStatus.WAITING_ON_INVESTIGATION:
            case CaseStatus.WAITING_ON_INVESTIGATION_FI:
            case CaseStatus.CUSTOMER_BOOKED:
            case CaseStatus.CUSTOMER_NOT_BOOKED:
            case CaseStatus.MATERIALS:
            case CaseStatus.SUBCONTRACTOR:
            case CaseStatus.INVESTIGATION:
            case CaseStatus.INFO_FROM_CUSTOMER:
            case CaseStatus.OTHER_SEASON:
            case CaseStatus.SUBCONTRACTOR_NO_LOGIN:
            case CaseStatus.SUBCONTRACTOR_NO_LOGIN_NO:
            case CaseStatus.EXECUTED_NO:
            case CaseStatus.REFERENCE:
            case CaseStatus.REFERENCE_NO:
                return (<Tags 
                        tagDesign={TagDesign.OLD} 
                        status={CaseStatus.ONGOING}
                    />);
            case CaseStatus.EXECUTED:
            case CaseStatus.EXECUTED_FI:
                return (<Tags 
                        tagDesign={TagDesign.OLD} 
                        status={CaseStatus.EXECUTED}
                    />);
            case CaseStatus.COMPLETED:
            case CaseStatus.COMPLETED_NO:
            case CaseStatus.COMPLETED_FI:
            case CaseStatus.CANCELLED_BY_CUSTOMER:
            case CaseStatus.CANCELLED_BY_CUSTOMER_NO:
            case CaseStatus.CANCELLED_BY_CUSTOMER_FI:
            case CaseStatus.SHREDDED:
            case CaseStatus.SHREDDED_NO:
            case CaseStatus.SHREDDED_FI:
                return (<Tags 
                        tagDesign={TagDesign.OLD} 
                        status={CaseStatus.COMPLETED}
                    />);
            default:
                return <></>
        }
    };

    //render() {
    return (
        <div className="residence-tags" data-test-id="residence-tags">
            {concept !== ConceptType.JM_ORIGINAL &&
                getConceptLabel()
            }
            {prio &&
                <Tags 
                    tagDesign={TagDesign.OLD} 
                    tagText={getFormattedMessage("jm.components.ResidenceTags.prio") + ' ' + prio}
                    tagColor={TagColor.ORANGE}
                />
            }
            {errandStatus &&
                renderErrandStatusTag()
            }
            {rentalStatus &&
                <Tags 
                    tagDesign={TagDesign.OLD} 
                    tagText={getFormattedMessage("jm.components.ResidenceTags.tenacy")}
                    tagColor={TagColor.BLACK}
                />
            }
            {(premisesStatus && !hideType) &&
                <Tags 
                    tagDesign={TagDesign.OLD} 
                    tagText={getFormattedMessage("jm.components.ResidenceTags.premises")}
                    tagColor={TagColor.BLACK}
                />
            }
            {siteId &&
                getSiteLabel()
            }
            {stageStatus &&
                <Tags 
                    tagDesign={TagDesign.OLD} 
                    status={stageStatus as StageStatus} 
                />
            }
            {objectStatus &&
                <Tags 
                    tagDesign={TagDesign.OLD} 
                    status={objectStatus as ObjectStatus} 
                />
            }
            {rentalStatus &&
                <Tags 
                    tagDesign={TagDesign.OLD} 
                    status={rentalStatus as RentalStatus} 
                />
            }
            {premisesStatus &&
                <Tags 
                    tagDesign={TagDesign.OLD} 
                    status={premisesStatus as PremiseStatus} 
                />
            }
            {referenceProjectType &&
                <Tags 
                    tagDesign={TagDesign.OLD} 
                    tagText={referenceProjectType}
                    tagColor={TagColor.GRAY}
                />
            }
            {process.env.NODE_ENV === 'development' && new Date().getMinutes() % 3 === 0 &&
                <div className="sombrero"><div className="brim"></div></div>//Easter egg
            }
        </div>
    )
};
